import { useMutationApi, useQueryApi } from 'utils/api'

import type { UseMutationOptions } from 'react-query'
import type { CommonResponse } from 'types/api'
import type {
  MaterialDataResponse,
  MateriReflection,
  ResponseMaterialTextData,
  ResponseMaterialVideoData,
  ResponseQuiz,
  PostRefleksiResponse,
  QuizAnswer,
} from 'types/microlearning'

const basePath = '/microlearnings'

export function useGetMateriText(
  moduleId: string,
  materialId: string,
  id: string
) {
  return useQueryApi<CommonResponse<ResponseMaterialTextData>>(
    `${basePath}/modules/${moduleId}/materials/${materialId}/texts/${id}`,
    {
      queryConfig: {
        enabled: !!id,
        retry: false,
      },
    }
  )
}

export function useGetMateriVideo(
  moduleId: string,
  materialId: string,
  id: string
) {
  return useQueryApi<CommonResponse<ResponseMaterialVideoData>>(
    `${basePath}/modules/${moduleId}/materials/${materialId}/videos/${id}`,
    {
      queryConfig: {
        enabled: !!id,
        retry: false,
      },
    }
  )
}

export function useGetReflection(
  moduleId: string | number,
  materialId: string | number
) {
  return useQueryApi<CommonResponse<MateriReflection>>(
    `${basePath}/modules/${moduleId}/materials/${materialId}/reflection`,
    {
      queryConfig: {
        enabled: !!moduleId && !!materialId,
        retry: false,
      },
    }
  )
}

export function useGetQuiz(
  moduleId: string | number,
  materialId: string | number
) {
  return useQueryApi<CommonResponse<ResponseQuiz>>(
    `${basePath}/modules/${moduleId}/materials/${materialId}/quiz`,
    {
      queryConfig: {
        enabled: !!moduleId && !!materialId,
        retry: false,
      },
    }
  )
}

type MateriProgressPayload = {
  id: string
  type: 'text' | 'video'
}

export function usePostMateriProgress(
  moduleId: string,
  materialId: string,
  opts?: UseMutationOptions<
    CommonResponse<ResponseMaterialVideoData | ResponseMaterialTextData>,
    Error,
    MateriProgressPayload
  >
) {
  return useMutationApi<
    CommonResponse<ResponseMaterialVideoData | ResponseMaterialTextData>,
    Error,
    MateriProgressPayload
  >(`${basePath}/modules/${moduleId}/materials/${materialId}/contents`, {
    mutationConfig: opts,
    axiosConfig: {
      method: 'POST',
    },
  })
}

export function usePostQuizProgress(
  moduleId: string,
  materialId: string,
  opts?: UseMutationOptions<
    CommonResponse<MaterialDataResponse>,
    Error,
    { quiz: QuizAnswer[] }
  >
) {
  return useMutationApi<
    CommonResponse<MaterialDataResponse>,
    Error,
    { quiz: QuizAnswer[] }
  >(`${basePath}/modules/${moduleId}/materials/${materialId}/quiz`, {
    mutationConfig: opts,
    axiosConfig: {
      method: 'POST',
    },
  })
}

export function usePostRefleksi(
  moduleId: string,
  materialId: string,
  opts?: UseMutationOptions<
    CommonResponse<PostRefleksiResponse>,
    Error,
    { answer: string }
  >
) {
  return useMutationApi<
    CommonResponse<PostRefleksiResponse>,
    Error,
    { answer: string }
  >(`${basePath}/modules/${moduleId}/materials/${materialId}/reflective`, {
    mutationConfig: opts,
    axiosConfig: {
      method: 'POST',
    },
  })
}
